import React, { useEffect, useState, useCallback, useMemo, useRef } from 'react';
import { apiFetch } from '../bizsuite-app';
import { toast } from '../utils/toast';
import FormField from '../components/FormField';
import SearchableSelect from '../components/SearchableSelect';
import ContractLineItemsEditor from '../components/ContractLineItemsEditor';
import BillingPlanEditor from '../components/BillingPlanEditor';
import RegionPicker from '../components/RegionPicker';
import PricingComputation from '../components/PricingComputation';

/**
 * Full-page Contract editor. Replaces the Contract modal.
 * Sections (collapsible): Header · Billing Terms · Line Items · Billing Plan.
 *
 * URL pattern: #/contracts/:id/edit or #/contracts/new
 */
export default function ContractEditor({ contractId: idFromProps }) {
  const [contractId, setContractId] = useState(() => idFromProps || parseIdFromHash());
  const isNew = !contractId || contractId === 'new';
  // When the hash ends in /view → read-only mode. Same full layout, inputs
  // disabled, no Update button, no + Add Line Item, no + Add row for the
  // billing plan. Back button still works; user can click "Edit" to switch.
  const [readOnly, setReadOnly] = useState(() => /\/contracts\/[^/]+\/view(\?|$)/.test(window.location.hash || ''));
  useEffect(() => {
    function onHash() {
      setReadOnly(/\/contracts\/[^/]+\/view(\?|$)/.test(window.location.hash || ''));
      setContractId(parseIdFromHash());
    }
    window.addEventListener('hashchange', onHash);
    return () => window.removeEventListener('hashchange', onHash);
  }, []);

  const [form, setForm] = useState(EMPTY);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [copying, setCopying] = useState(false);
  const [customers, setCustomers] = useState([]);
  const [opportunities, setOpportunities] = useState([]);
  const [projects, setProjects] = useState([]);
  const [pricingSchemas, setPricingSchemas] = useState([]);
  const [paymentTerms, setPaymentTerms] = useState([]);   // [{ id, name, days }]

  /* Refs for sub-editors that need to flush unsaved state when the user
     clicks the top-level "Update" button. Child components register a
     savePending() function via { current: fn }. */
  const billingPlanSaverRef = useRef(null);
  const lineItemsSaverRef = useRef(null);

  /* ─── Load all references + (if editing) the contract ─── */
  useEffect(() => {
    (async () => {
      try {
        const [custRes, oppRes, projRes, psRes, ptRes] = await Promise.all([
          apiFetch('/api/customers?limit=200'),
          apiFetch('/api/crm/opportunities?limit=200'),
          apiFetch('/api/projects?limit=500'),
          apiFetch('/api/pricing-schemas'),
          apiFetch('/api/metadata/lookups?category=payment_terms'),
        ]);
        if (custRes.ok) setCustomers((await custRes.json()).customers || []);
        if (oppRes.ok)  setOpportunities((await oppRes.json()).opportunities || []);
        if (ptRes && ptRes.ok) {
          const d = await ptRes.json();
          setPaymentTerms((d.rows || d.lookups || []).filter(r => r.status !== 'Inactive'));
        }
        if (projRes.ok) setProjects((await projRes.json()).projects || []);
        if (psRes.ok)   setPricingSchemas((await psRes.json()).schemas || []);
      } catch (_) { /* ignore */ }

      if (!isNew) {
        try {
          const r = await apiFetch(`/api/contracts/${contractId}`);
          if (r.ok) {
            const d = await r.json();
            const c = d.contract || d;
            setForm({
              contract_number: c.contract_number || '', customer_id: c.customer_id || '',
              opportunity_id: c.opportunity_id || '', estimate_id: c.estimate_id || '',
              project_id: c.project_id || '', type: c.type || '',
              total_value: c.total_value || '', negotiated_value: c.negotiated_value || '',
              currency: c.currency || 'INR',
              start_date: c.start_date ? c.start_date.substring(0, 10) : '',
              end_date: c.end_date ? c.end_date.substring(0, 10) : '',
              status: c.status || 'draft',
              document_url: c.document_url || '', po_number: c.po_number || '',
              po_document_url: c.po_document_url || '',
              contract_document_url: c.contract_document_url || '',
              billing_terms: c.billing_terms || '', payment_terms: c.payment_terms || '',
              payment_term_id: c.payment_term_id || '',
              pricing_schema_id: c.pricing_schema_id || '',
              region_id: c.region_id || '',
            });
          }
        } catch (_) { /* ignore */ }
      }
      setLoading(false);
    })();
  }, [contractId, isNew]);

  function set(k, v) {
    setForm(p => {
      const next = { ...p, [k]: v };
      // Spec: "schema – from customer master". When the user picks a
      // customer and the contract doesn't already have a pricing schema,
      // default it from that customer's master record. Never overwrite an
      // explicit choice — the user can still pick a different schema below.
      if (k === 'customer_id' && v) {
        const cust = customers.find(c => c.id === v);
        if (cust && cust.pricing_schema_id && !p.pricing_schema_id) {
          next.pricing_schema_id = cust.pricing_schema_id;
        }
        // Default the payment term too — same overwrite-only-when-empty rule.
        if (cust && cust.payment_term_id && !p.payment_term_id) {
          next.payment_term_id = cust.payment_term_id;
        }
      }
      return next;
    });
  }

  async function save() {
    if (!form.contract_number.trim()) { toast.error('Contract number is required'); return; }
    setSaving(true);
    const body = {
      contractNumber: form.contract_number, customerId: form.customer_id || null,
      opportunityId: form.opportunity_id || null, type: form.type || null,
      totalValue: form.total_value || 0, currency: form.currency,
      startDate: form.start_date || null, endDate: form.end_date || null,
      status: form.status, documentUrl: form.document_url || null,
      estimateId: form.estimate_id || null, proposalId: form.proposal_id || null,
      projectId: form.project_id || null, negotiatedValue: form.negotiated_value || null,
      poNumber: form.po_number || null, poDocumentUrl: form.po_document_url || null,
      contractDocumentUrl: form.contract_document_url || null,
      billingTerms: form.billing_terms || null, paymentTerms: form.payment_terms || null,
      paymentTermId: form.payment_term_id || null,
      pricingSchemaId: form.pricing_schema_id || null,
      regionId: form.region_id || null,
    };
    try {
      const url = isNew ? '/api/contracts' : `/api/contracts/${contractId}`;
      const method = isNew ? 'POST' : 'PUT';
      const r = await apiFetch(url, { method, body: JSON.stringify(body) });
      if (r.ok) {
        const d = await r.json().catch(() => ({}));
        if (isNew && d.id) {
          window.location.hash = `#/contracts/${d.id}/edit`;
          setContractId(d.id);
          toast.success('Contract saved');
        } else {
          // Existing contract — also flush sub-editors so that line items and
          // billing-plan milestones the user touched get persisted by the
          // same top-level click. Each sub-editor registers its own silent
          // save function via a ref; it's a no-op if there are no changes.
          const subResults = await Promise.allSettled([
            billingPlanSaverRef.current ? billingPlanSaverRef.current() : Promise.resolve({ ok: true }),
            lineItemsSaverRef.current   ? lineItemsSaverRef.current()   : Promise.resolve({ ok: true }),
          ]);
          const anyErr = subResults.find(s => s.status === 'rejected' || (s.value && s.value.error));
          if (anyErr) {
            const msg = anyErr.status === 'rejected' ? anyErr.reason?.message : anyErr.value?.error;
            toast.error(`Contract saved — but sub-sections failed: ${msg}`);
          } else {
            toast.success('Contract, line items & billing plan saved');
          }
        }
      } else {
        const d = await r.json().catch(() => ({}));
        toast.error(d.error || 'Save failed');
      }
    } catch (e) { toast.error(e.message); }
    setSaving(false);
  }

  /* Destructive copy from the linked estimate. Wipes current line items +
     billing plan on this contract, then re-copies from the estimate.
     Requires explicit confirmation. Reloads the page by bumping the hash
     so line-item and billing-plan sub-editors re-fetch. */
  async function copyFromEstimate() {
    if (!contractId || !form.estimate_id) { toast.error('No estimate linked to this contract'); return; }
    const ok = await toast.confirm({
      title: 'Copy from estimate?',
      body: 'This replaces the current line items and billing plan on this contract with those from the linked estimate. Any edits you have made to them will be lost.',
      confirmLabel: 'Replace with estimate',
      danger: true,
    });
    if (!ok) return;
    setCopying(true);
    try {
      const r = await apiFetch(`/api/contracts/${contractId}/copy-from-estimate`, { method: 'POST' });
      const d = await r.json().catch(() => ({}));
      if (r.ok) {
        toast.success(`Copied ${d.lines_copied || 0} line item(s) and ${d.milestones_copied || 0} milestone(s). Reloading…`);
        // Sub-editors cache their own data; easiest way to refresh is a
        // full page reload. The toast stays on screen long enough to read.
        setTimeout(() => window.location.reload(), 600);
      } else {
        toast.error(d.error || 'Copy failed');
      }
    } catch (e) { toast.error(e.message); }
    setCopying(false);
  }

  const custOptions = useMemo(() => customers.map(c => ({ value: c.id, label: c.name || c.company_name || c.id })), [customers]);
  const oppOptions  = useMemo(() => opportunities.map(o => ({ value: o.id, label: o.name })), [opportunities]);
  const projOptions = useMemo(() =>
    projects.filter(p => !form.customer_id || !p.customer_id || p.customer_id === form.customer_id)
            .map(p => ({ value: p.id, label: p.name || p.project_code || p.id })),
    [projects, form.customer_id]
  );
  const pricingOptions = useMemo(() => [
    { value: '', label: '— None —' },
    ...pricingSchemas
      .filter(s => s.status === 'Active')
      .map(s => ({ value: s.id, label: `${s.code} · ${s.name}` })),
  ], [pricingSchemas]);
  const paymentTermOptions = useMemo(() => [
    { value: '', label: '— None —' },
    ...paymentTerms.map(t => ({
      value: t.id,
      label: t.days != null ? `${t.name} (${t.days}d)` : t.name,
    })),
  ], [paymentTerms]);

  if (loading) return <div style={{ padding: 40, color: '#94A3B8' }}>Loading contract…</div>;

  return (
    // Estimates-style layout: full-width single panel, no maxWidth /
    // centred wrapper. The hard `overflow-x: hidden` is a defensive
    // backstop — when async data loads (Pricing Schema names with long
    // codes, Opportunity names, etc.) the native <select> elements can
    // briefly try to size to their longest option and push the cell
    // wider; clamping at the page level absorbs that.
    <div style={{ padding: 20, width: '100%', minWidth: 0, maxWidth: '100%',
                  overflowX: 'hidden', boxSizing: 'border-box' }}>
      {/* ── Toolbar ── */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 18, flexWrap: 'wrap' }}>
        <button onClick={() => { window.location.hash = '#/contracts'; }} style={btnGhost}>← Back</button>
        <h1 style={{ margin: 0, fontSize: 18, flex: 1 }}>
          {isNew
            ? 'New Contract'
            : (readOnly
                ? `View Contract — ${form.contract_number || '…'}`
                : `Edit Contract — ${form.contract_number || '…'}`)}
          {form.status && <span style={{ marginLeft: 10, fontSize: 11, padding: '2px 8px', borderRadius: 999, background: 'rgba(255,255,255,0.06)', color: '#94A3B8' }}>{form.status}</span>}
          {readOnly && <span style={{ marginLeft: 8, fontSize: 11, padding: '2px 8px', borderRadius: 999, background: 'rgba(59,141,248,0.18)', color: '#60A5FA', border: '1px solid rgba(59,141,248,0.3)' }}>Read-only</span>}
        </h1>
        {readOnly ? (
          <button onClick={() => { window.location.hash = `#/contracts/${contractId}/edit`; }} style={btnPrimary}>Edit</button>
        ) : (
          <>
            {!isNew && form.estimate_id && (
              <button onClick={copyFromEstimate} disabled={copying || saving} style={btnGhost}
                title="Re-copy line items and billing plan from the linked estimate. Replaces current contract lines and plan.">
                {copying ? 'Copying…' : '⇐ Copy from Estimate'}
              </button>
            )}
            <button onClick={() => save()} disabled={saving} style={btnPrimary}>{saving ? 'Saving…' : (isNew ? 'Create' : 'Update')}</button>
          </>
        )}
      </div>

      {/* Wrapping in <fieldset disabled> cascades to all native inputs /
          selects / textareas inside, so the view route is read-only with
          a single flag. SearchableSelect / custom components also honour
          `disabled` where we pass it through below. */}
      {/* min-width: 0 + max-width: 100% break the browser default
          (`fieldset { min-width: min-content }`) which otherwise sizes
          the fieldset to its widest descendant — in this case the
          milestone table — and pushes the whole contract page wider
          than the viewport. This was the actual cause of the page-level
          horizontal scroll. */}
      <fieldset disabled={readOnly} style={{ border: 'none', padding: 0, margin: 0,
        minWidth: 0, maxWidth: '100%', width: '100%',
        opacity: readOnly ? 0.85 : 1 }}>

      {/* ── Header ── */}
      {/* Explicit 2-column grid: the global .form-row class uses a 3-col
          grid that leaves a dangling empty cell on this 2-field layout and
          makes the page look unbalanced on wide monitors. Inline grid keeps
          both columns evenly sized and stops the Opportunity field from
          appearing to "run away" on larger screens. */}
      <Section title="Header">
        <div style={gridTwoCol}>
          <FormField label="Contract Number" name="contract_number" value={form.contract_number} onChange={(n, v) => set(n, v)} required />
          <FormField label="Type" name="type" type="select" value={form.type} onChange={(n, v) => set(n, v)} options={CONTRACT_TYPES} />
          <SearchableSelect label="Customer" value={form.customer_id} onChange={v => set('customer_id', v)} options={custOptions} placeholder="Select customer..." />
          <SearchableSelect label="Opportunity" value={form.opportunity_id} onChange={v => set('opportunity_id', v)} options={oppOptions} placeholder="Select opportunity..." />
          <FormField label="Project" name="project_id" type="select" value={form.project_id} onChange={(n, v) => set(n, v)} options={projOptions} placeholder={projOptions.length === 0 ? 'No projects available' : 'Select project...'} />
          <FormField label="PO Number" name="po_number" value={form.po_number} onChange={(n, v) => set(n, v)} />
          <FormField label="Total Value" name="total_value" type="number" value={form.total_value} onChange={(n, v) => set(n, v)} min="0" />
          <FormField label="Negotiated Value" name="negotiated_value" type="number" value={form.negotiated_value} onChange={(n, v) => set(n, v)} min="0" />
          <FormField label="Currency" name="currency" value={form.currency} onChange={(n, v) => set(n, v)} />
          <FormField label="Payment Terms" name="payment_term_id" type="select"
            value={form.payment_term_id} onChange={(n, v) => set(n, v)}
            options={paymentTermOptions}
            placeholder={paymentTermOptions.length <= 1 ? 'Define payment terms in System → Metadata → Lookups' : 'Select payment terms...'} />
          <FormField label="Start Date" name="start_date" type="date" value={form.start_date} onChange={(n, v) => set(n, v)} />
          <FormField label="End Date" name="end_date" type="date" value={form.end_date} onChange={(n, v) => set(n, v)} />
          <FormField label="Status" name="status" type="select" value={form.status} onChange={(n, v) => set(n, v)} options={CONTRACT_STATUSES} />
          <FormField label="Document URL" name="document_url" value={form.document_url} onChange={(n, v) => set(n, v)} />
          <FormField label="Pricing Schema" name="pricing_schema_id" type="select" value={form.pricing_schema_id}
            onChange={(n, v) => set(n, v)} options={pricingOptions}
            placeholder={pricingOptions.length <= 1 ? 'No pricing schemas defined yet' : 'Select pricing schema...'} />
          <RegionPicker label="Jurisdiction (Location Region)" value={form.region_id}
            onChange={(v) => set('region_id', v)} placeholder="Select jurisdiction region..." />
        </div>
      </Section>

      {/* ── Pricing Computation ── */}
      {!isNew && form.pricing_schema_id && (
        <Section title="Pricing Computation">
          <PricingComputation
            schemaId={form.pricing_schema_id}
            contractId={contractId}
            jurisdictionRegionId={form.region_id || null}
            negotiatedValue={form.negotiated_value}
            totalValue={form.total_value}
            currency={form.currency}
          />
        </Section>
      )}

      {/* ── Line Items ── */}
      <Section title={
        <span>
          Line Items &amp; Sub-items
          {' '}
          <span style={{
            color: '#F59E0B',
            fontSize: 10,
            fontWeight: 500,
            textTransform: 'none',
            letterSpacing: '0.2px',
            marginLeft: 8,
          }}>
            Line Items are items in your proposal that you can bill &amp; Sub-items are the internal breakup to determine effort
          </span>
        </span>
      }>
        {isNew ? (
          <EmptyBox>Save the contract first to add line items.</EmptyBox>
        ) : (
          <ContractLineItemsEditor contractId={contractId} contractStart={form.start_date} contractEnd={form.end_date} />
        )}
      </Section>

      {/* ── Billing Plan ── */}
      <Section title="Billing Plan">
        {isNew ? (
          <EmptyBox>Save the contract first to configure the billing plan.</EmptyBox>
        ) : (
          // Pass the effective pricing base — Negotiated → Total — so
          // milestone "Value" amounts match the Pricing Computation panel.
          <BillingPlanEditor ownerType="contract" ownerId={contractId}
            totalValue={parseFloat(form.negotiated_value) || parseFloat(form.total_value) || 0}
            currency={form.currency} readOnly={readOnly} saverRef={billingPlanSaverRef} />
        )}
      </Section>

      {/* ── Billing Terms (moved to the bottom — long free-form notes belong
           after the structured numbers, not before them) ── */}
      <Section title="Billing Terms">
        <FormField label="Billing Terms" name="billing_terms" type="textarea" value={form.billing_terms} onChange={(n, v) => set(n, v)} />
      </Section>

      </fieldset>
    </div>
  );
}

/* Billing Plan editor moved to ../components/BillingPlanEditor.jsx so it
   can be shared between contract and estimate pages. */

/* ───────── helpers ───────── */
function parseIdFromHash() {
  if (typeof window === 'undefined') return null;
  const m = (window.location.hash || '').match(/\/contracts\/([^/]+)/);
  return m ? m[1] : null;
}
function ordinal(n) {
  const s = ['th', 'st', 'nd', 'rd'], v = n % 100;
  return n + (s[(v - 20) % 10] || s[v] || s[0]);
}
function Section({ title, children }) {
  // overflow: hidden on the section card stops any wide child (Billing
  // Plan table, Pricing Computation table) from pushing past the
  // section's right edge. Each inner scroller (`overflow-x: auto`) then
  // owns its own horizontal scrollbar, scoped to that section.
  return (
    <div style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 12, padding: 16, marginBottom: 14, minWidth: 0, maxWidth: '100%', overflow: 'hidden' }}>
      <div style={{ fontSize: 11, letterSpacing: '0.6px', textTransform: 'uppercase', color: '#94A3B8', fontWeight: 700, marginBottom: 12 }}>{title}</div>
      {children}
    </div>
  );
}
function EmptyBox({ children }) {
  return <div style={{ padding: 14, color: '#94A3B8', fontSize: 12.5, textAlign: 'center', border: '1px dashed rgba(255,255,255,0.08)', borderRadius: 8 }}>{children}</div>;
}
function Th({ children, align }) { return <th style={{ textAlign: align || 'left', fontSize: 10.5, fontWeight: 700, textTransform: 'uppercase', color: '#94A3B8', letterSpacing: '0.4px', padding: '8px 6px' }}>{children}</th>; }
function Td({ children, align, style }) { return <td style={{ padding: '6px', textAlign: align || 'left', ...(style || {}) }}>{children}</td>; }

const EMPTY = {
  contract_number: '', customer_id: '', opportunity_id: '', estimate_id: '', project_id: '',
  type: '', total_value: '', negotiated_value: '', currency: 'INR',
  start_date: '', end_date: '', status: 'draft',
  document_url: '', po_number: '', po_document_url: '', contract_document_url: '',
  billing_terms: '', payment_terms: '', payment_term_id: '',
  pricing_schema_id: '', region_id: '',
};
const CONTRACT_STATUSES = ['draft', 'active', 'completed', 'expired', 'terminated'];
const CONTRACT_TYPES = ['Fixed Price', 'T&M', 'Milestone', 'Mixed', 'AMS', 'Staff Augmentation'];

// Auto-fit grid — fits as many ~280px-min cells as the row can hold,
// each capped at 1fr. Tracks collapse from 2 columns to 1 automatically
// when the available width gets tight, so the form never forces the
// page to scroll horizontally regardless of viewport size or sidebar
// state. This mirrors the Estimates pattern.
const gridTwoCol = {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
  gap: '14px 24px',
  alignItems: 'start',
};
const inp = { padding: '6px 8px', borderRadius: 6, border: '1px solid rgba(255,255,255,0.10)', background: 'rgba(255,255,255,0.04)', color: '#E5E7EB', fontSize: 12, outline: 'none' };
const btnPrimary = { padding: '8px 14px', borderRadius: 7, fontSize: 12.5, fontWeight: 600, border: 'none', cursor: 'pointer', background: 'linear-gradient(135deg, #FF00E5, #BF5FFF)', color: '#fff' };
const btnGhost   = { padding: '8px 12px', borderRadius: 7, fontSize: 12.5, fontWeight: 600, border: '1px solid rgba(255,255,255,0.10)', background: 'rgba(255,255,255,0.04)', color: '#E5E7EB', cursor: 'pointer' };
const btnApprove = { padding: '5px 12px', borderRadius: 6, fontSize: 11, fontWeight: 700, border: 'none', cursor: 'pointer', background: 'linear-gradient(135deg, #10B981, #3B8FF4)', color: '#fff' };
const linkBtn    = { all: 'unset', cursor: 'pointer', color: '#60A5FA', fontSize: 11.5 };
